/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  CumulativeUsageByNetwork,
  UsageByNetwork,
  UsageByLink,
  TotalUsageByNetwork,
  TotalUsageByLink,
  TotalPacketLossByLink,
  PacketLossByLink,
  ServiceAvailability,
} from '../../shared/entities/dataUsage';
import { handleErrorInterceptor } from '../../utils/AuthUtils';
import { RootState } from '../index';
import { Action, ActionCreators } from './actions';

const receivedTotalUsageByNetworkSuccess = (ajaxResponse: AjaxResponse<TotalUsageByNetwork>): any => {
  return ActionCreators.ReceivedTotalUsageByNetworkSuccess.create(ajaxResponse.response);
};

const receivedTotalUsageByNetworkError = (error: AjaxError): any => {
  return ActionCreators.ReceivedTotalUsageByNetworkError.create(error);
};

export const requestTotalUsageByNetwork = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestTotalUsageByNetwork.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/terminals/usage/by-network/total', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedTotalUsageByNetworkSuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedTotalUsageByNetworkError(error))
              ),
          ),
        ),
    ),
  );
};

const receivedTotalUsageByLinkSuccess = (ajaxResponse: AjaxResponse<TotalUsageByLink>): any => {
  return ActionCreators.ReceivedTotalUsageByLinkSuccess.create(ajaxResponse.response);
};

const receivedTotalUsageByLinkError = (error: AjaxError): any => {
  return ActionCreators.ReceivedTotalUsageByLinkError.create(error);
};

export const requestTotalUsageByLink = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestTotalUsageByLink.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/terminals/usage/by-link/total', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedTotalUsageByLinkSuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedTotalUsageByLinkError(error))
              ),
          ),
        ),
    ),
  );
};
const receivedUsageByLinkSuccess = (ajaxResponse: AjaxResponse<UsageByLink>): any => {
  return ActionCreators.ReceivedUsageByLinkSuccess.create(ajaxResponse.response);
};

const receivedUsageByLinkError = (error: AjaxError): any => {
  return ActionCreators.ReceivedUsageByLinkError.create(error);
};

export const requestUsageByLink = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestUsageByLink.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/terminals/usage/by-link', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedUsageByLinkSuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedUsageByLinkError(error))
              ),
          ),
        ),
    ),
  );
};

const receivedUsageByLinkCumulativeSuccess = (ajaxResponse: AjaxResponse<UsageByLink>): any => {
  return ActionCreators.ReceivedUsageByLinkCumulativeSuccess.create(ajaxResponse.response);
};

const receivedUsageByLinkCumulativeError = (error: AjaxError): any => {
  return ActionCreators.ReceivedUsageByLinkCumulativeError.create(error);
};

export const requestUsageByLinkCumulative = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestUsageByLinkCumulative.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/terminals/usage/by-link/cumulative', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedUsageByLinkCumulativeSuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedUsageByLinkCumulativeError(error))
              ),
          ),
        ),
    ),
  );
};

const receivedUsageByNetworkSuccess = (ajaxResponse: AjaxResponse<UsageByNetwork>): any => {
  return ActionCreators.ReceivedUsageByNetworkSuccess.create(ajaxResponse.response);
};

const receivedUsageByNetworkError = (error: AjaxError): any => {
  return ActionCreators.ReceivedUsageByNetworkError.create(error);
};

export const requestUsageByNetwork = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestUsageByNetwork.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/terminals/usage/by-network', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedUsageByNetworkSuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedUsageByNetworkError(error))
              ),
          ),
        ),
    ),
  );
};

const receivedCumulativeUsageByNetworkSuccess = (ajaxResponse: AjaxResponse<CumulativeUsageByNetwork>): any => {
  return ActionCreators.ReceivedCumulativeUsageByNetworkSuccess.create(ajaxResponse.response);
};

const receivedCumulativeUsageByNetworkError = (error: AjaxError): any => {
  return ActionCreators.ReceivedCumulativeUsageByNetworkError.create(error);
};

export const requestCumulativeUsageByNetwork = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestCumulativeUsageByNetwork.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/terminals/usage/by-network/cumulative', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedCumulativeUsageByNetworkSuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedCumulativeUsageByNetworkError(error))
              ),
          ),
        ),
    ),
  );
};

// Packet Loss
const receivedTotalPacketLossByLinkSuccess = (ajaxResponse: AjaxResponse<TotalPacketLossByLink>): any => {
  return ActionCreators.ReceivedTotalPacketLossByLinkSuccess.create(ajaxResponse.response);
};

const receivedTotalPacketLossByLinkError = (error: AjaxError): any => {
  return ActionCreators.ReceivedTotalPacketLossByLinkError.create(error);
};

export const requestTotalPacketLossByLink = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestTotalPacketLossByLink.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/packetloss/total', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedTotalPacketLossByLinkSuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedTotalPacketLossByLinkError(error))
              ),
          ),
        ),
    ),
  );
};

const receivedPacketLossByLinkSuccess = (ajaxResponse: AjaxResponse<PacketLossByLink>): any => {
  return ActionCreators.ReceivedPacketLossByLinkSuccess.create(ajaxResponse.response);
};

const receivedPacketLossByLinkError = (error: AjaxError): any => {
  return ActionCreators.ReceivedPacketLossByLinkError.create(error);
};

export const requestPacketLossByLink = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestPacketLossByLink.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/packetloss', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedPacketLossByLinkSuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedPacketLossByLinkError(error))
              ),
          ),
        ),
    ),
  );
};

const receivedServiceAvailabilitySuccess = (ajaxResponse: AjaxResponse<ServiceAvailability>): any => {
  return ActionCreators.ReceivedServiceAvailabilitySuccess.create(ajaxResponse.response);
};

const receivedServiceAvailabilityError = (error: AjaxError): any => {
  return ActionCreators.ReceivedServiceAvailabilityError.create(error);
};

export const requestServiceAvailability = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestServiceAvailability.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/service-availability', $action.payload).pipe(
          map((ajaxResponse: AjaxResponse<any>): Action => receivedServiceAvailabilitySuccess(ajaxResponse)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(receivedServiceAvailabilityError(error))
              ),
          ),
        ),
    ),
  );
};
